import React, { useEffect } from "react";
import Image from "next/image";
import Icon from "@/components/icons";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useInView } from "react-intersection-observer";
import styles from "./index.module.css";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useTranslations from "@/i18n/useTranslations";

interface BenefitsModuleProps {
  location?: string;
  schoolName?: string;
  benefitsModule?: {
    enabled?: boolean;
    title?: string;
    benefitsIconTheme?: "light" | "dark";
    benefitsBulletList?: {
      title: string;
      text: string;
      icon: string;
      tooltip?: string;
    }[];
    benefitsImage?: "default" | "alternative" | "detailPage";
  };
}

export default function BenefitsModule({
  schoolName,
  location = "detail-page",
  benefitsModule,
}: BenefitsModuleProps) {
  const t = useTranslations("Common");
  const { benefitsIconTheme, benefitsBulletList, benefitsImage } =
    benefitsModule ?? {};
  const certificateBenefitsImage =
    benefitsImage === "alternative"
      ? "/images/certificate-benefits-2.jpg"
      : benefitsImage === "detailPage"
        ? "/images/certificate-benefits-3.jpg"
        : "/images/certificate-benefits-1.jpg";

  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: "benefits-module",
      name: "benefits-module",
      location: location,
      text: benefitsModule?.title || "Certificate Benefits",
      htmlId: "benefits-module",
    },
  });

  const { ref: benefitsRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const hasTriggeredViewEvent = React.useRef(false);

  useEffect(() => {
    if (inView && !hasTriggeredViewEvent.current) {
      elementViewed();
      hasTriggeredViewEvent.current = true;
    }
  }, [inView, elementViewed]);

  return (
    <div
      ref={benefitsRef}
      className={`${styles.containerquery} ${styles.benefitsmodule} w-full flex flex-row gap-6 md:gap-2 bg-white rounded-xl border border-putty-400`}
    >
      <div className="flex flex-col md:py-10 md:px-10 lg:pr-2 p-4 gap-6">
        <h2 className="m-0 p-0 text-2xl lg:text-3xl font-bold text-primary-500 leading-learn-hero-line-height">
          {benefitsModule?.title || t("certificateBenefitsAlt")}
        </h2>
        <div className="w-full flex flex-col md:flex-row gap-6 md:gap-0">
          <div
            className={`flex-1 ${benefitsIconTheme === "light" ? "p-0" : "md:pl-6"}`}
          >
            {benefitsBulletList && benefitsBulletList?.length > 0 && (
              <ul className="!px-0 flex flex-col gap-10">
                {benefitsBulletList.map((benefit, index) => (
                  <li
                    key={index}
                    className={`flex flex-row md:items-center gap-4 ${benefitsIconTheme === "light" ? "p-2" : "p-0"}`}
                  >
                    <div className="flex">
                      <div
                        className={`flex h-[84px] w-[83px] flex-shrink-0 flex-grow-0 items-center justify-center rounded-xl
                          ${benefitsIconTheme === "light" ? "bg-transparent" : "bg-secondary-500 text-white"}`}
                      >
                        <Icon
                          type={benefit.icon || "circle-check-fill"}
                          svgProps={{
                            width: benefitsIconTheme === "light" ? "64" : "56",
                            height: benefitsIconTheme === "light" ? "64" : "56",
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col flex-1">
                      <span className="text-md md:text-lg text-primary-500 font-bold">
                        {benefit.title}
                      </span>
                      <span className="text-md md:text-lg text-primary-500">
                        {sanitizeAndParseInlineHTML(
                          benefit.text
                            ? benefit.text.replace(
                                /\[school-name\]/g,
                                `<strong>${schoolName}</strong>`,
                              )
                            : "",
                          false,
                        )}
                        {benefit.tooltip && (
                          <Popover>
                            <PopoverTrigger>
                              <div className="h-4 w-4 ml-1">
                                <Icon
                                  type="info"
                                  svgProps={{ width: "20", height: "20" }}
                                />
                              </div>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="rounded-none max-w-[160px]"
                            >
                              {sanitizeAndParseInlineHTML(benefit.tooltip)}
                            </PopoverContent>
                          </Popover>
                        )}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <div
        className={`${styles.benefitsimage} flex-shrink-0 flex items-end justify-end`}
      >
        <Image
          src={certificateBenefitsImage}
          alt={t("certificateBenefitsAlt")}
          width={418}
          height={352}
          className="w-[418px] h-[352px] object-contain rounded-br-xl "
        />
      </div>
    </div>
  );
}
